import { useCallback, useMemo, useRef, useState } from 'react'
import { MediaCapture } from '../utils/mediaCapture'

interface UseScreenShareReturn {
  isCapturing: boolean
  isSupported: boolean
  startScreenShare: (dataChannel: RTCDataChannel | null) => Promise<void>
  stopScreenShare: (dataChannel: RTCDataChannel | null) => void
  toggleScreenShare: (dataChannel: RTCDataChannel | null) => Promise<void>
}

const checkScreenShareSupport = (): boolean => {
  return (
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices &&
    typeof navigator.mediaDevices.getDisplayMedia === 'function'
  )
}

export const useScreenShare = (
  screenShareCapture: MediaCapture | null,
): UseScreenShareReturn => {
  const [isCapturing, setIsCapturing] = useState(false)
  const isSupported = useMemo(() => checkScreenShareSupport(), [])
  // Use ref instead of state to prevent race conditions and stale closures in PiP window
  const isCapturingRef = useRef(false)
  const currentStream = useRef<MediaStream | null>(null)

  const stopScreenShare = useCallback(
    (dataChannel: RTCDataChannel | null) => {
      // Stop all tracks in the current stream
      if (currentStream) {
        currentStream.current?.getTracks().forEach(track => {
          track.stop()
          console.log('Stopped track:', track.kind)
        })
        currentStream.current = null
        screenShareCapture?.setStream(null)
        setIsCapturing(false)
        isCapturingRef.current = false

        if (dataChannel) {
          const command = {
            type: 'stop_video',
            data: null,
          }
          console.log('Sending stop_video command', command)
          const message = JSON.stringify(command)
          dataChannel.send(message)
        }
      }
    },
    [currentStream],
  )

  const startScreenShare = async (dataChannel: RTCDataChannel | null) => {
    if (isCapturingRef.current) return

    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          frameRate: {
            max: 1,
          },
          width: {
            max: 1280,
          },
          height: {
            max: 720,
          },
        },
        audio: false,
      })

      screenShareCapture?.setStream(stream)
      currentStream.current = stream
      setIsCapturing(true)
      isCapturingRef.current = true

      // Add ended handler for when user stops sharing via browser popup
      const videoTrack = stream.getVideoTracks()[0]
      if (videoTrack) {
        videoTrack.addEventListener('ended', () => {
          stopScreenShare(dataChannel)
        })
      }

      if (dataChannel) {
        const command = {
          type: 'start_video',
          data: null,
        }
        const message = JSON.stringify(command)
        dataChannel.send(message)
        console.log('Sending start_video command', command)
      }

      // Initialize stream with ended handler (like client.js)
    } catch (error) {
      console.error('Screen capture failed:', error)
      throw error
    }
  }

  const toggleScreenShare = useCallback(
    async (dataChannel: RTCDataChannel | null) => {
      if (isCapturing) {
        stopScreenShare(dataChannel)
      } else {
        await startScreenShare(dataChannel)
      }
    },
    [isCapturing, startScreenShare, stopScreenShare],
  )

  return {
    isCapturing,
    isSupported,
    startScreenShare,
    stopScreenShare,
    toggleScreenShare,
  }
}
